<script setup lang="ts">
import {
  DataSetDto,
  instanceOfEnumerationDataSetDto,
  instanceOfNumericDataSetDto,
} from '@/api/generator';

import EnumerationTrendChart from '@/components/TrendChart/EnumerationTrendChart.vue';
import NumericTrendChart from '@/components/TrendChart/NumericTrendChart.vue';
import DataSetSelection from '@/views/TrendsSelectionView/components/DataSetSelection.vue';
import * as Highcharts from 'highcharts';
import { computed, ref, watch } from 'vue';
import { api } from '@/api';
import Button from '@/components/buttons/Button.vue';
import { anyFile } from '@/components/icons/icons';

interface TrendChartOverviewProps {
  totalDataSetCount: number;
  dataSets: DataSetDto[];
  startDate?: string;
  endDate?: string;
  showDifferences: boolean;
}

const props = defineProps<TrendChartOverviewProps>();

const selectedDataSets = ref(props.dataSets.map((dataSet) => dataSet.id));

const enumerationDatasets = computed(() =>
  props.dataSets.filter((dataSet) => instanceOfEnumerationDataSetDto(dataSet)),
);
const numericDatasets = computed(() =>
  props.dataSets.filter((dataSet) => instanceOfNumericDataSetDto(dataSet)),
);

watch(selectedDataSets, (newSelectedDataSets) => {
  Highcharts.charts.forEach((chart) => {
    chart?.series.forEach((series) => {
      if (series.options.id) {
        const shouldBeVisible = newSelectedDataSets.includes(series.options.id);
        if (series.visible !== shouldBeVisible) {
          series.setVisible(shouldBeVisible);
        }
      }
    });
  });
});

async function downloadCsv() {
  const responseWrapper = await api.dataSetApi.getDataPointsCsv({
    dataPointRequestDto: { dataSetIds: selectedDataSets.value },
  });

  if (!responseWrapper.success) {
    console.error('CSV file could not be generated');
    return;
  }

  const response = responseWrapper.raw as any;
  const csvText = await response.text();

  // get the filename
  const disposition = response.headers.get('content-disposition');
  let filename = 'data.csv';

  if (disposition && disposition.includes('filename')) {
    const filenameRegex = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/;
    const matches = disposition.match(filenameRegex);
    if (matches) {
      filename = matches[1] ? decodeURIComponent(matches[1]) : matches[2];
    }
  }

  const blob = new Blob([csvText], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  a.remove();
  window.URL.revokeObjectURL(url);
}
</script>

<template>
  <div class="trend-chart-overview-wrapper">
    <template v-if="numericDatasets.length">
      <NumericTrendChart
        class="mt-2"
        :datasets="numericDatasets"
        :startDate="startDate"
        :endDate="endDate"
      />
    </template>

    <template v-if="enumerationDatasets.length">
      <EnumerationTrendChart
        class="mt-2"
        :datasets="enumerationDatasets"
        :startDate="startDate"
        :endDate="endDate"
      />
    </template>
    <!-- combined download button  -->
    <div class="d-flex justify-end">
      <hr class="w-100 mt-11 mb-6 hr" />
      <Transition name="fade" mode="out-in">
        <Button
          v-if="selectedDataSets.length"
          variant="text"
          class="mt-5 ml-1 mr-1"
          color="secondary"
          size="large"
          :icon="anyFile"
          :label="$t('DownloadCsv')"
          @click="downloadCsv"
        />
      </Transition>
      <hr class="w-100 mt-11 hr" />
    </div>
    <div class="dataset-selection-container">
      <DataSetSelection
        v-model:selectedDataSets="selectedDataSets"
        :totalDataSetCount="totalDataSetCount"
        class="mt-4"
        :dataSets="dataSets"
        :showDifferences="showDifferences"
        showAsLegend
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.trend-chart-overview-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
}

.dataset-selection-container {
  max-width: calc(100vw - 48px);
  position: relative;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity,
    0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  scale: 0.1;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  scale: 1;
}

.hr {
  height: 1px;
  background-color: rgb(var(--v-theme-primary-lighten-4));
  border: none;
}
</style>
